import React from 'react';
import { useTranslation } from 'react-i18next';

import { bulkUpdateExistingAiAssistantMute, getExistingExternalConversationMuteStats } from '../services/companyService';
import type { AssistantBulkMuteChannel, ExternalConversationAiMuteStats } from '../types/Company';
import { ASSISTANT_MUTE_CHANNELS } from '../utils/assistantMuteChannels';
import styles from './AssistantChannelMutePanels.module.css';

type BulkMuteActionKey = `${'mute' | 'unmute'}:${AssistantBulkMuteChannel | 'all'}`;

const CHANNEL_CARD_CLASS: Record<AssistantBulkMuteChannel, string> = {
  whatsapp: styles.cardWhatsapp,
  instagram: styles.cardInstagram,
  telegram: styles.cardTelegram,
  messenger: styles.cardMessenger,
  webchat: styles.cardWebchat,
  tiktok: styles.cardTiktok,
  voice: styles.cardVoice,
};

const emptyStats: ExternalConversationAiMuteStats = {
  totalUnmutedCount: 0,
  unmutedCountByChannel: {},
};

interface AssistantChannelExistingMutePanelProps {
  stats: ExternalConversationAiMuteStats;
  onStatsChange: (stats: ExternalConversationAiMuteStats) => void;
  onNotice: (message: string) => void;
  onError: (message: string) => void;
}

export const AssistantChannelExistingMutePanel: React.FC<AssistantChannelExistingMutePanelProps> = ({
  stats = emptyStats,
  onStatsChange,
  onNotice,
  onError,
}) => {
  const { t } = useTranslation('companies');
  const [actionKey, setActionKey] = React.useState<BulkMuteActionKey | null>(null);

  const getUnmutedCount = React.useCallback((channel: AssistantBulkMuteChannel | null) => {
    if (!channel) {
      return stats.totalUnmutedCount;
    }

    return stats.unmutedCountByChannel[channel] ?? 0;
  }, [stats]);

  const renderUnmutedCount = (channel: AssistantBulkMuteChannel | null) => (
    <span className={styles.unmutedCountBadge}>
      {t('assistantBulkMuteUnmutedCount', { count: getUnmutedCount(channel) })}
    </span>
  );

  const runBulkUpdate = React.useCallback(async (
    muted: boolean,
    channel: AssistantBulkMuteChannel | null,
  ) => {
    const key: BulkMuteActionKey = `${muted ? 'mute' : 'unmute'}:${channel ?? 'all'}`;
    setActionKey(key);
    onError('');

    try {
      const result = await bulkUpdateExistingAiAssistantMute({
        muted,
        channel: channel ?? undefined,
      });

      const refreshedStats = await getExistingExternalConversationMuteStats();
      onStatsChange(refreshedStats);

      const channelLabel = channel
        ? t(ASSISTANT_MUTE_CHANNELS.find(item => item.key === channel)!.labelKey)
        : t('assistantBulkMuteAllChannels');

      if (result.updatedConversationCount > 0) {
        onNotice(
          t(muted ? 'assistantBulkMuteSuccess' : 'assistantBulkUnmuteSuccess', {
            count: result.updatedConversationCount,
            channel: channelLabel,
          }),
        );
      } else {
        onNotice(
          t(muted ? 'assistantBulkMuteAlreadyApplied' : 'assistantBulkUnmuteAlreadyApplied', {
            channel: channelLabel,
          }),
        );
      }
    } catch (error) {
      onError(error instanceof Error ? error.message : t('assistantBulkMuteFailed'));
    } finally {
      setActionKey(null);
    }
  }, [onError, onNotice, onStatsChange, t]);

  const isBusy = actionKey !== null;

  return (
    <section className={styles.section}>
      <div className={styles.sectionHeader}>
        <h4 className={styles.sectionTitle}>{t('assistantBulkMuteHeading')}</h4>
        <p className={styles.sectionDescription}>{t('assistantBulkMuteDescription')}</p>
      </div>

      <div className={`${styles.card} ${styles.cardFeatured}`}>
        <div className={styles.cardCopy}>
          <span className={styles.channelName}>{t('assistantBulkMuteAllChannels')}</span>
          <span className={styles.channelHint}>{t('assistantBulkMuteAllChannelsHelp')}</span>
          {renderUnmutedCount(null)}
        </div>
        <div className={styles.actions}>
          <button
            type="button"
            className={styles.muteButton}
            disabled={isBusy}
            onClick={() => void runBulkUpdate(true, null)}
          >
            {actionKey === 'mute:all' ? t('assistantBulkMuteWorking') : t('assistantBulkMuteAction')}
          </button>
          <button
            type="button"
            className={styles.unmuteButton}
            disabled={isBusy}
            onClick={() => void runBulkUpdate(false, null)}
          >
            {actionKey === 'unmute:all' ? t('assistantBulkMuteWorking') : t('assistantBulkUnmuteAction')}
          </button>
        </div>
      </div>

      <div className={styles.grid}>
        {ASSISTANT_MUTE_CHANNELS.map(channel => (
          <div key={channel.key} className={`${styles.card} ${CHANNEL_CARD_CLASS[channel.key]}`}>
            <div className={styles.cardCopy}>
              <span className={styles.channelName}>{t(channel.labelKey)}</span>
              {renderUnmutedCount(channel.key)}
            </div>
            <div className={styles.actions}>
              <button
                type="button"
                className={styles.muteButton}
                disabled={isBusy}
                onClick={() => void runBulkUpdate(true, channel.key)}
              >
                {actionKey === `mute:${channel.key}` ? t('assistantBulkMuteWorking') : t('assistantBulkMuteAction')}
              </button>
              <button
                type="button"
                className={styles.unmuteButton}
                disabled={isBusy}
                onClick={() => void runBulkUpdate(false, channel.key)}
              >
                {actionKey === `unmute:${channel.key}` ? t('assistantBulkMuteWorking') : t('assistantBulkUnmuteAction')}
              </button>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

export default AssistantChannelExistingMutePanel;
